import { createMultiStyleConfigHelpers, StyleFunctionProps } from '@chakra-ui/react';

const accordion = createMultiStyleConfigHelpers([
  'root',
  'container',
  'button',
  'panel',
  'icon',
]).defineMultiStyleConfig({
  baseStyle: {},
  sizes: {},
  variants: {
    unstyled: {
      container: {
        border: 'none',
      },
      button: {
        px: 'unset',
        _hover: {
          bgColor: 'white',
        },
      },
      panel: {
        px: 'unset',
      },
    },
  },
  defaultProps: {},
});

const button = {
  baseStyle: {
    borderRadius: 'base',
  },
  sizes: {},
  variants: {
    solid: (props: StyleFunctionProps) => ({
      color: props.colorScheme === 'gray' ? 'gray.600' : undefined,
      px: 'lg',
      _disabled: {
        bgColor: 'gray.100',
        color: 'gray.500',
      },
      _hover: {
        _disabled: {
          bgColor: 'gray.100',
          color: 'gray.500',
        },
      },
    }),
    ghost: (props: StyleFunctionProps) => ({
      color: props.colorScheme === 'gray' ? 'gray.600' : undefined,
      px: 'lg',
    }),
    link: (props: StyleFunctionProps) => ({
      color: props.colorScheme === 'gray' ? 'gray.600' : undefined,
    }),
    outline: (props: StyleFunctionProps) => ({
      color: props.colorScheme === 'gray' ? 'gray.600' : undefined,
      px: 'lg',
      bgColor: 'white',
      _disabled: {
        borderColor: 'gray.300',
        color: 'gray.500',
      },
      _hover: {
        bgColor: 'white',
        _disabled: {
          borderColor: 'gray.300',
          color: 'gray.500',
        },
      },
      _active: {
        bgColor: 'white',
      },
    }),
  },
  defaultProps: {
    colorScheme: 'gray',
  },
};

const drawer = createMultiStyleConfigHelpers([
  'overlay',
  'dialogContainer',
  'dialog',
  'header',
  'closeButton',
  'body',
  'footer',
]).defineMultiStyleConfig({
  baseStyle: {},
  sizes: {
    xs: {
      dialog: {
        maxW: '268px',
      },
    },
    sm: {
      dialog: {
        maxW: '360px',
      },
    },
  },
  variants: {},
  defaultProps: {
    size: 'xs',
  },
});

const floatingLabel = {
  transform: 'scale(0.85) translateY(-26px)',
};

const form = createMultiStyleConfigHelpers(['container', 'requiredIndicator', 'helperText']).defineMultiStyleConfig({
  baseStyle: {},
  sizes: {},
  variants: {
    floating: {
      container: {
        _focusWithin: {
          label: {
            ...floatingLabel,
          },
        },
        'input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, textarea:not(:placeholder-shown) ~ label':
          {
            ...floatingLabel,
          },
        label: {
          top: 0,
          left: 0,
          zIndex: 2,
          position: 'absolute',
          backgroundColor: 'white',
          color: 'gray.400',
          pointerEvents: 'none',
          mx: 3,
          px: 1,
          my: 3,
          transformOrigin: 'left top',
        },
      },
    },
  },
  defaultProps: {},
});

const input = createMultiStyleConfigHelpers(['addon', 'field', 'element', 'group']).defineMultiStyleConfig({
  baseStyle: {},
  sizes: {},
  variants: {
    outline: {
      field: {
        bgColor: 'white',
        borderRadius: 'base',
        _focus: {
          borderColor: 'red.500',
          boxShadow: '0px 0px 0px 1px var(--chakra-colors-red-500)',
        },
        _disabled: {
          bgColor: 'gray.50',
          color: 'gray.500',
          cursor: 'not-allowed',
        },
      },
    },
    flushed: {
      field: {
        bgColor: 'white',
        _focus: {
          borderColor: 'red.500',
          boxShadow: '0px 1px 0px 0px var(--chakra-colors-red-500)',
        },
        _disabled: {
          bgColor: 'gray.50',
          color: 'gray.500',
          cursor: 'not-allowed',
        },
      },
    },
  },
  defaultProps: {
    variant: 'flushed',
  },
});

const modal = {
  parts: ['body', 'dialog', 'footer', 'header', 'overlay', 'closeButton', 'dialogContainer'],
  baseStyle: {},
  sizes: {
    sm: {
      dialog: {
        maxW: '384px',
      },
    },
    md: {
      dialog: {
        maxW: '448px',
      },
    },
  },
  variants: {
    primary: {
      dialog: {
        borderRadius: 'md',
        boxShadow: 'none',
      },
      header: {
        px: 'xl',
        pt: 'xl',
        pb: 'sm',
        color: 'red.500',
        fontSize: '2xl',
      },
      body: {
        px: 'xl',
        py: 'sm',
      },
      footer: {
        px: 'xl',
        pb: 'xl',
        pt: 'sm',
      },
      overlay: {
        bg: 'rgba(0,0,0,0.30)',
      },
    },
    secondary: {
      dialog: {
        borderRadius: 'xl',
        boxShadow: 'xl',
      },
      header: {
        px: 'lg',
        pt: 'lg',
        pb: 'sm',
        color: 'red.500',
        fontSize: '2xl',
      },
      body: {
        px: 'lg',
        py: 'sm',
      },
      footer: {
        px: 'lg',
        pb: 'lg',
        pt: 'sm',
      },
      overlay: {
        bg: 'rgba(0,0,0,0.10)',
      },
    },
  },
  defaultProps: {
    variant: 'primary',
    size: '2xl',
    isCentered: true,
    closeOnOverlayClick: false,
  },
};

const pinInput = {
  baseStyle: {},
  sizes: {},
  variants: {
    flushed: {
      bgColor: 'white',
      _focusVisible: {
        borderColor: 'red.500',
        boxShadow: '0px 1px 0px 0px var(--chakra-colors-red-500)',
      },
    },
  },
  defaultProps: {
    size: 'lg',
    variant: 'flushed',
  },
};

const popover = {
  parts: ['body', 'footer', 'header', 'content', 'closeButton', 'popper', 'arrow'],
  baseStyle: {
    content: {
      w: 'fit-content',
      border: '1px solid',
      borderColor: 'gray.200',
      boxShadow: 'sm',
      bgColor: 'white',
      borderRadius: 'base',
      _focus: {
        boxShadow: 'none',
      },
    },
    body: {
      w: '100%',
      p: 'sm',
    },
  },
  sizes: {},
  variants: {},
  defaultProps: {},
};

const switchComponent = {
  parts: ['track', 'container', 'thumb'],
  baseStyle: {
    container: {
      mb: '0px',
    },
    track: {
      _focus: {
        boxShadow: '0px 0px 0px 3px var(--chakra-colors-red-100)',
      },
    },
  },
  sizes: {},
  variants: {},
  defaultProps: {
    size: 'lg',
    colorScheme: 'red',
  },
};

const stickyHeaderVariant = {
  thead: {
    position: 'sticky',
    top: '0',
    bgColor: 'white',
    zIndex: 10,
  },
  th: {
    fontWeight: 'bold',
    fontSize: 'sm',
    textTransform: 'capitalize',
  },
};

const table = {
  parts: ['caption', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr'],
  baseStyle: {},
  sizes: {
    xs: {
      thead: {
        fontWeight: 'semibold',
      },
      th: {
        padding: 'xs',
        fontSize: '2xs',
      },
      td: {
        padding: 'xs',
        fontSize: '2xs',
      },
      caption: {
        fontSize: '2xs',
        marginTop: 'sm',
      },
    },
  },
  variants: {
    simple: stickyHeaderVariant,
    striped: stickyHeaderVariant,
  },
  defaultProps: {
    colorScheme: 'gray',
    variant: 'simple',
    size: 'sm',
  },
};

const tabs = {
  parts: ['tab', 'tabpanel', 'tabpanels', 'tablist', 'root', 'indicator'],
  baseStyle: {
    tabpanels: {
      mt: 'md',
    },
  },
  sizes: {},
  variants: {},
  defaultProps: {
    colorScheme: 'red',
    size: 'lg',
  },
};

const textarea = {
  baseStyle: {},
  sizes: {},
  variants: {
    outline: () => ({
      _focus: {
        borderColor: 'red.500',
      },
      _focusVisible: {
        boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
      },
    }),
  },
  defaultProps: {},
};

const tooltip = {
  baseStyle: {
    maxW: 'lg',
  },
  sizes: {},
  variants: {},
  defaultProps: {},
};

export const components = {
  Button: button,
  Table: table,
  Modal: modal,
  Input: input,
  Switch: switchComponent,
  Form: form,
  Popover: popover,
  PinInput: pinInput,
  Tabs: tabs,
  Textarea: textarea,
  Tooltip: tooltip,
  Drawer: drawer,
  Accordion: accordion,
};
